/**
 * @module utils/visuals/noise
 * @description Cheap deterministic value noise + curl for canvas flow fields.
 * No allocation per call — safe to run thousands of times per frame.
 */

function hash2(ix: number, iy: number): number {
  let n = Math.imul(ix, 374761393) + Math.imul(iy, 668265263)
  n = Math.imul(n ^ (n >>> 13), 1274126177)
  return ((n ^ (n >>> 16)) >>> 0) / 4294967295
}

/** 2D value noise in 0..1 with smoothstep interpolation. */
export function valueNoise(x: number, y: number): number {
  const ix = Math.floor(x)
  const iy = Math.floor(y)
  const fx = x - ix
  const fy = y - iy
  const ux = fx * fx * (3 - 2 * fx)
  const uy = fy * fy * (3 - 2 * fy)
  const a = hash2(ix, iy)
  const b = hash2(ix + 1, iy)
  const c = hash2(ix, iy + 1)
  const d = hash2(ix + 1, iy + 1)
  return a + (b - a) * ux + (c - a) * uy + (a - b - c + d) * ux * uy
}

const EPS = 0.55

/**
 * Divergence-free curl of the value-noise field.
 * Returns a unit-ish vector — particles orbit and braid instead of collapsing.
 */
export function curl(x: number, y: number, t: number, out: [number, number]): [number, number] {
  const tx = x + t
  const ty = y - t * 0.6
  const n1 = valueNoise(tx, ty + EPS)
  const n2 = valueNoise(tx, ty - EPS)
  const n3 = valueNoise(tx + EPS, ty)
  const n4 = valueNoise(tx - EPS, ty)
  out[0] = (n1 - n2) / (2 * EPS)
  out[1] = -(n3 - n4) / (2 * EPS)
  return out
}

/** Flow angle at a point — used to orient ticks along the field. */
export function flowAngle(x: number, y: number, t: number): number {
  return valueNoise(x + t, y - t * 0.6) * Math.PI * 2
}
